import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import { WHATSAPP_DEPARTMENTS, buildWhatsAppUrl, formatPhoneDisplay, SITE } from '../../config/site';

const FloatingWhatsApp = ({ language: propLanguage }) => {
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState(propLanguage || 'en');
  const popupRef = useRef(null);

  useEffect(() => {
    if (propLanguage) {
      setLanguage(propLanguage);
      return;
    }
    const stored = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
    if (stored) setLanguage(stored);
    const onStorage = (e) => {
      if (e.key === 'language' && e.newValue) setLanguage(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [propLanguage]);

  const t = {
    en: {
      open: 'Chat with us on WhatsApp',
      title: 'Chat with IMAC',
      subtitle: 'Pick a department to start a conversation.',
      defaultMsg: `Hello ${SITE.name}, I would like more information.`,
    },
    fr: {
      open: 'Discutez avec nous sur WhatsApp',
      title: 'Discuter avec IMAC',
      subtitle: 'Choisissez un département pour commencer.',
      defaultMsg: `Bonjour ${SITE.name}, j'aimerais plus d'informations.`,
    },
  }[language] || {};

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={popupRef} className="fixed bottom-5 right-5 z-50 flex flex-col items-end">
      {open && (
        <div className="mb-3 w-[320px] max-w-[calc(100vw-2.5rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-bottom-2">
          <div className="bg-[#075E54] text-white px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="MessageCircle" size={20} />
                <h3 className="font-semibold">{t.title}</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-white/80 hover:text-white"
                aria-label="Close"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <p className="text-xs text-white/80 mt-1">{t.subtitle}</p>
          </div>
          <div className="p-2 bg-[#ECE5DD]">
            {WHATSAPP_DEPARTMENTS.map((d) => (
              <a
                key={d.key}
                href={buildWhatsAppUrl(d.number, t.defaultMsg)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex items-center space-x-3 p-3 mb-2 last:mb-0 bg-white rounded-xl hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 rounded-full bg-[#25D366]/10 flex items-center justify-center text-[#075E54] flex-shrink-0">
                  <Icon name={d.icon} size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {d.label[language] || d.label.en}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {d.role[language] || d.role.en} • {formatPhoneDisplay(d.number)}
                  </p>
                </div>
                <Icon name="ChevronRight" size={18} className="text-gray-400 group-hover:text-[#25D366] group-hover:translate-x-0.5 transition-all" />
              </a>
            ))}
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t.open}
        className="w-14 h-14 rounded-full bg-[#25D366] text-white shadow-2xl hover:shadow-[0_0_25px_rgba(37,211,102,0.5)] hover:scale-105 active:scale-95 transition-all flex items-center justify-center relative"
      >
        <Icon name={open ? 'X' : 'MessageCircle'} size={28} />
        {!open && (
          <span className="absolute top-0 right-0 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#25D366] border-2 border-white"></span>
          </span>
        )}
      </button>
    </div>
  );
};

export default FloatingWhatsApp;
